import { Element, useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import { useSelector } from "react-redux";
import type { Speaker } from "src/types";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
  BlockPaddingWrapper,
} from "@/components/editor/shared/block-wrapper";
import SyncedSpeakersSettings from "@/components/editor/shared/speakers/speakers-settings/synced-speakers-settings-component";
import { getAllSpeakers } from "@/features/speakers/speakers-reducer";

import SyncedSpeakerCard from "./speaker-card/synced-speaker-card-component";
import SpeakersList from "./speakers-list-component";

/**
 * `SPEAKERS_LIST_ID` is the id we use to identify the speakers list component,
 * useful when we need to append SpeakerCards to the list.
 */

export type SpeakerLayout = "large-cards";

type SyncedSpeakersProps = {
  speakers?: Speaker[];
  layout: SpeakerLayout;
};

export default function SyncedSpeakers(props: SyncedSpeakersProps) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();
  const dashboardSpeakers = useSelector(getAllSpeakers);
  const speakers = props.speakers ?? dashboardSpeakers;

  const isEmpty = speakers.length === 0;
  const { enabled } = useEditor((state) => ({
    enabled: state.isEnabled(),
  }));

  if (!enabled && isEmpty) return null;

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <BlockPaddingWrapper>
        <BlockMaxWidthWrapper>
          <div className="flex justify-center">
            <Element
              id="speakers-title"
              is={Text}
              element="h2"
              text="Speakers"
              pattern="headingTwo"
              data-testid="speakers-title"
            />
          </div>
          <SpeakersList layout={props.layout}>
            {enabled && isEmpty && <SyncedSpeakersSettings minimal />}
            {(speakers || []).map((speaker) => (
              <SyncedSpeakerCard
                key={speaker.id + speaker.name}
                speaker={speaker}
                layout={props.layout}
                isBioAvailable={!!speaker.bio}
              />
            ))}
          </SpeakersList>
        </BlockMaxWidthWrapper>
      </BlockPaddingWrapper>
    </BlockBackgroundColorWrapper>
  );
}

SyncedSpeakers.craft = {
  custom: {
    displayName: "Synced Speakers",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
    settings: SyncedSpeakersSettings,
  },
};
